import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MeasurementsProvider, { useMeasurementsDispatch, useMeasurementsState } from './MeasurementsProvider';
import { useMeasurementsData } from './useMeasurementsData';
import GradingValidationModal from './GradingValidationModal';
import MeasurementsGrid from './MeasurementsGrid';
import MeasurementsFloatingActions from './MeasurementsFloatingActions';
import ConfirmationModal from '../../../../../../Components/ModalPopup/ConfirmationModal';
import Loader from '../../../../../../Components/Utility/Loader/Loader';
import './measurements.css';

interface MeasurementsModuleProps {
  productIds: string[];
}

const MeasurementsModuleInner: React.FC<MeasurementsModuleProps> = ({ productIds }) => {
  const { t } = useTranslation('bulk-edit');
  const history = useHistory();
  const state = useMeasurementsState();
  const dispatch = useMeasurementsDispatch();
  const { proceedAfterConfirm, save, clearHighlights, loadMore } = useMeasurementsData(productIds);

  if (state.phase === 'validating' || state.phase === 'loading') {
    return (
      <div className="bulk-measure-loader">
        <Loader />
      </div>
    );
  }
  if (state.phase === 'error') {
    return <div className="bulk-measure-status">{state.error ?? t('MEASUREMENTS_MODULE.ERROR')}</div>;
  }
  if (state.phase === 'empty') {
    return <div className="bulk-measure-status">{t('MEASUREMENTS_MODULE.NO_GRADING_GROUP')}</div>;
  }
  if (state.phase === 'needs-confirm') {
    return (
      <GradingValidationModal
        ineligibleProducts={state.ineligibleProducts}
        onConfirm={proceedAfterConfirm}
        onCancel={() => history.goBack()}
      />
    );
  }
  if (state.products.length === 0) {
    return <div className="bulk-measure-status">{t('MEASUREMENTS_MODULE.EMPTY')}</div>;
  }
  return (
    <>
      <MeasurementsGrid onLoadMore={loadMore} />
      <MeasurementsFloatingActions onSave={save} onClearHighlights={clearHighlights} />
      {state.showEditCapWarning && (
        <ConfirmationModal
          show
          title={t('MEASUREMENTS_MODULE.EDIT_CAP_TITLE')}
          message={t('MEASUREMENTS_MODULE.EDIT_CAP_MESSAGE')}
          confirmLabel={t('MEASUREMENTS_MODULE.SAVE')}
          onConfirm={() => {
            dispatch({ type: 'DISMISS_EDIT_CAP_WARNING' });
            save().catch(() => undefined);
          }}
          onClose={() => dispatch({ type: 'DISMISS_EDIT_CAP_WARNING' })}
        />
      )}
    </>
  );
};

/** Bulk Edit → Measurements module: owns its own render-scoped state (isolated from other modules). */
const MeasurementsModule: React.FC<MeasurementsModuleProps> = ({ productIds }) => (
  <MeasurementsProvider>
    <MeasurementsModuleInner productIds={productIds} />
  </MeasurementsProvider>
);

export default MeasurementsModule;
